import { logErrorToSentry } from '@modules/common/utils';
import { getApolloClient } from 'ApolloClient';
import dayjs from 'dayjs';
import Locker from 'lockr';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { useFeatureVariable } from 'v2/core/clients/featureFlag';
import { FeatureFlagVariable } from 'v2/core/clients/featureFlag/flags';
import { clearApolloCache } from 'v2/utils/clearCache';
import { ExpiryConfigVariable } from './types';

const CLEAR_CACHE_DONE = 'clear_cache_done';
const trackClearCacheEvent = createAnalyticsTracker('clearCache');

export const ClearCacheFeatureFlag: FeatureFlagVariable<ExpiryConfigVariable> =
  {
    name: 'clear_cache',
    variableName: 'config',
    fallback: {
      uid: null,
      expiry: null,
    },
  };

export function ClearCacheIfRequired() {
  const navigate = useNavigate();
  const [, { uid, expiry }] = useFeatureVariable(ClearCacheFeatureFlag);

  useEffect(() => {
    if (!uid) {
      return;
    }

    if (expiry) {
      const expiryTimestamp = dayjs(expiry).valueOf();
      const now = Date.now();

      if (now > expiryTimestamp) {
        return;
      }
    }

    const clearCacheKey = `${CLEAR_CACHE_DONE}_${uid}`;
    const isClearCacheDone = Locker.get(clearCacheKey);
    if (!isClearCacheDone) {
      Locker.set(clearCacheKey, true);

      requestAnimationFrame(async () => {
        try {
          await clearApolloCache();
          const apolloClient = getApolloClient();
          await apolloClient.resetStore();

          trackClearCacheEvent({
            eventName: 'Clear Cache',
          });
        } catch (e) {
          logErrorToSentry({ errorObj: e, message: 'failed to clear cache' });
        }
      });
    }
  }, [uid, expiry, navigate]);

  return null;
}
